import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { CalendarDays, ChevronLeft, ChevronRight, Plus, Info, CalendarPlus, Clapperboard, MapPin, Clock, Trash2, Loader2, FileDown, CalendarCheck, RefreshCw, Link2, Copy, Check, ExternalLink } from "lucide-react";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  isSameMonth, isSameDay, addMonths, format, parseISO,
} from "date-fns";
import { de } from "date-fns/locale";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAccount } from "@/context/AccountContext";
import { STATUS_STYLE, STATUS_LABEL, TYPE_LABEL } from "@/lib/constants";
import { ContentDrawer } from "@/components/ContentDrawer";
import { thumbUrl } from "@/components/FileUpload";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

export default function Kalender() {
  const { accountKey, refreshApprovals } = useOutletContext();
  const { user } = useAuth();
  const { current } = useAccount();
  const isAdmin = user?.role === "admin";
  const myId = user?.id || user?._id;
  const [items, setItems] = useState([]);
  const [shootDays, setShootDays] = useState([]);
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [cursor, setCursor] = useState(new Date());
  const [selected, setSelected] = useState(null);
  const [scheduleDay, setScheduleDay] = useState(null);
  const [shootDialog, setShootDialog] = useState(null); // { day } | { existing }
  const [eventDialog, setEventDialog] = useState(null); // { day } | { existing }
  const [icalDialog, setIcalDialog] = useState(false);

  const load = () => api.get("/content", { params: { account: accountKey } }).then(({ data }) => setItems(data));
  const loadShootDays = () => api.get("/shoot-days", { params: { account: accountKey } }).then(({ data }) => setShootDays(data));
  const loadEvents = () => api.get("/events", { params: { account: accountKey } }).then(({ data }) => setEvents(data));

  useEffect(() => {
    if (!accountKey) return;
    load();
    loadShootDays();
    loadEvents();
    api.get("/chat/members").then(({ data }) => setMembers(data)).catch(() => {});
  }, [accountKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [cursor]);

  const itemsByDay = (day) =>
    items.filter((i) => i.scheduled_date && isSameDay(parseISO(i.scheduled_date), day));
  const shootsByDay = (day) =>
    shootDays.filter((s) => s.date && isSameDay(parseISO(s.date), day));
  const eventsByDay = (day) =>
    events.filter((e) => {
      try { return e.date && isSameDay(parseISO(e.date), day); } catch { return false; }
    });

  const scheduledCount = items.filter((i) => i.scheduled_date).length;

  return (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto animate-rise">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400 flex items-center gap-1.5">
            <CalendarDays className="h-3.5 w-3.5" /> Kalender
          </p>
          <h1 className="font-head text-4xl font-bold text-neutral-900 tracking-tighter mt-1">Veröffentlichungsplanung</h1>
          <p className="text-sm text-neutral-400 mt-1">{scheduledCount} geplante Inhalte · {shootDays.length} Drehtag{shootDays.length === 1 ? "" : "e"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          <button
            data-testid="cal-ical-subscribe"
            onClick={() => setIcalDialog(true)}
            className="h-9 px-3.5 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 text-sm font-semibold hover:bg-blue-100 flex items-center gap-1.5 order-first"
            title="Diesen Kanal als automatisch aktualisierten Kalender abonnieren"
          >
            <Link2 className="h-4 w-4" /> iCal-Abo
          </button>
          <button
            data-testid="cal-office-sync"
            onClick={async () => {
              try {
                const { data } = await api.post("/events/sync-from-office", { account_key: accountKey });
                toast.success(`Office-Board abgeglichen: ${data.synced} Event${data.synced === 1 ? "" : "s"} übernommen`);
                loadEvents();
              } catch (e) {
                toast.error(e.response?.data?.detail || "Office-Sync fehlgeschlagen");
              }
            }}
            className="h-9 px-3.5 rounded-lg border border-neutral-200 bg-white text-neutral-700 text-sm font-semibold hover:bg-neutral-50 flex items-center gap-1.5"
            title="Events aus dem Office-Board (office.lokal-bruchkoebel.de) übernehmen"
          >
            <RefreshCw className="h-4 w-4" /> Office-Sync
          </button>
          <button
            data-testid="cal-pdf"
            onClick={async () => {
              try {
                const { data } = await api.get("/calendar/pdf", {
                  params: { account: accountKey, month: format(cursor, "yyyy-MM") },
                  responseType: "blob", timeout: 60000,
                });
                const url = URL.createObjectURL(data);
                const a = document.createElement("a");
                a.href = url; a.download = `kalender-${format(cursor, "yyyy-MM")}.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 30000);
              } catch { toast.error("PDF-Export fehlgeschlagen"); }
            }}
            className="h-9 px-3.5 rounded-lg border border-neutral-200 bg-white text-neutral-700 text-sm font-semibold hover:bg-neutral-50 flex items-center gap-1.5"
            title="Monatsübersicht als PDF"
          >
            <FileDown className="h-4 w-4" /> PDF
          </button>
          <button
            data-testid="cal-new-event"
            onClick={() => setEventDialog({ day: new Date() })}
            className="h-9 px-3.5 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-700 text-sm font-semibold hover:bg-emerald-100 flex items-center gap-1.5"
          >
            <CalendarDays className="h-4 w-4" /> Event
          </button>
          {isAdmin && (
            <button
              data-testid="cal-new-shootday"
              onClick={() => setShootDialog({ day: new Date() })}
              className="h-9 px-3.5 rounded-lg bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 flex items-center gap-1.5"
            >
              <Clapperboard className="h-4 w-4" /> Drehtag
            </button>
          )}
          <button data-testid="cal-prev" onClick={() => setCursor(addMonths(cursor, -1))} className="h-9 w-9 rounded-lg border border-neutral-200 bg-white hover:bg-neutral-50 flex items-center justify-center">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="font-head font-semibold text-neutral-800 w-40 text-center capitalize">
            {format(cursor, "MMMM yyyy", { locale: de })}
          </span>
          <button data-testid="cal-next" onClick={() => setCursor(addMonths(cursor, 1))} className="h-9 w-9 rounded-lg border border-neutral-200 bg-white hover:bg-neutral-50 flex items-center justify-center">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-2.5 rounded-xl bg-blue-50 border border-blue-100 px-4 py-3">
        <Info className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
        <p className="text-[13px] text-blue-900 leading-relaxed">
          Jeder freigegebene oder geplante Inhalt erscheint an seinem Veröffentlichungstag — per <span className="font-semibold">+</span> planst du Inhalte ein.
          <span className="font-semibold text-amber-700"> 🎬 Drehtage</span> zeigen dem Team, wann gedreht wird — zugewiesene Creator werden automatisch benachrichtigt.
          <span className="font-semibold text-emerald-700"> 📅 Events</span> legst du direkt hier an (ohne Umweg über die Pipeline) — sie erscheinen auch im Event-Radar.
        </p>
      </div>

      <div className="mt-6 bg-white rounded-2xl shadow-[0_2px_10px_rgba(0,0,0,0.04)] overflow-hidden">
        <div className="grid grid-cols-7 border-b border-neutral-100">
          {WEEKDAYS.map((d) => (
            <div key={d} className="px-3 py-2.5 text-[11px] font-bold uppercase tracking-wider text-neutral-400">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((day) => {
            const dayItems = itemsByDay(day);
            const dayShoots = shootsByDay(day);
            const inMonth = isSameMonth(day, cursor);
            const today = isSameDay(day, new Date());
            return (
              <div
                key={day.toISOString()}
                className={`group min-h-[112px] border-b border-r border-neutral-100 p-1.5 ${inMonth ? "bg-white" : "bg-neutral-50/60"}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className={`text-xs font-semibold h-6 w-6 flex items-center justify-center rounded-full ${today ? "bg-neutral-900 text-white" : inMonth ? "text-neutral-600" : "text-neutral-300"}`}>
                    {format(day, "d")}
                  </div>
                  <div className="flex items-center opacity-0 group-hover:opacity-100 transition">
                    <button
                      data-testid={`cal-event-${format(day, "yyyy-MM-dd")}`}
                      onClick={() => setEventDialog({ day })}
                      className="h-6 w-6 rounded-md flex items-center justify-center text-neutral-300 hover:bg-emerald-50 hover:text-emerald-600"
                      title="Event anlegen"
                    >
                      <CalendarDays className="h-3.5 w-3.5" />
                    </button>
                    {isAdmin && (
                      <button
                        data-testid={`cal-shootday-${format(day, "yyyy-MM-dd")}`}
                        onClick={() => setShootDialog({ day })}
                        className="h-6 w-6 rounded-md flex items-center justify-center text-neutral-300 hover:bg-amber-50 hover:text-amber-600"
                        title="Drehtag anlegen"
                      >
                        <Clapperboard className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      data-testid={`cal-add-${format(day, "yyyy-MM-dd")}`}
                      onClick={() => setScheduleDay(day)}
                      className="h-6 w-6 rounded-md flex items-center justify-center text-neutral-300 hover:bg-blue-50 hover:text-blue-600"
                      title="Inhalt einplanen"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="space-y-1">
                  {dayShoots.map((s) => {
                    const mine = (s.creator_ids || []).includes(String(myId));
                    return (
                      <button
                        key={s.id}
                        data-testid={`cal-shoot-${s.id}`}
                        onClick={() => setShootDialog({ existing: s })}
                        className={`w-full text-left text-[11px] font-bold px-1.5 py-1 rounded border truncate transition hover:opacity-80 ${mine ? "bg-amber-500 text-white border-amber-600" : "bg-amber-50 text-amber-800 border-amber-200"}`}
                        title={`Drehtag: ${s.title}`}
                      >
                        🎬 {s.time_from ? `${s.time_from} · ` : ""}{s.title}
                      </button>
                    );
                  })}
                  {eventsByDay(day).slice(0, 2).map((e) => (
                    <button
                      key={e.id}
                      data-testid={`cal-eventitem-${e.id}`}
                      onClick={() => setEventDialog({ existing: e })}
                      className={`w-full text-left text-[11px] font-semibold px-1.5 py-1 rounded border truncate transition hover:opacity-80 ${e.source === "office" ? "bg-sky-50 text-sky-800 border-sky-200" : "bg-emerald-50 text-emerald-800 border-emerald-200"}`}
                      title={`${e.source === "office" ? "Office-Board-Event" : "Event"}: ${e.title}`}
                    >
                      {e.source === "office" ? "🏢" : "📅"} {e.time ? `${e.time} · ` : ""}{e.title}
                    </button>
                  ))}
                  {dayItems.slice(0, 3).map((i) => (
                    <button
                      key={i.id}
                      data-testid={`cal-item-${i.id}`}
                      onClick={() => setSelected(i)}
                      className={`w-full text-left text-[11px] font-medium px-1.5 py-1 rounded border truncate hover:opacity-80 transition ${STATUS_STYLE[i.status]}`}
                      title={i.title}
                    >
                      <span className="opacity-70">{TYPE_LABEL[i.content_type]} · </span>{i.title}
                    </button>
                  ))}
                  {dayItems.length > 3 && <div className="text-[10px] text-neutral-400 pl-1">+{dayItems.length - 3} mehr</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <ContentDrawer item={selected} onClose={() => setSelected(null)} onChanged={() => { load(); refreshApprovals?.(); }} />
      <ScheduleDialog
        day={scheduleDay}
        items={items}
        onClose={() => setScheduleDay(null)}
        onScheduled={() => { load(); refreshApprovals?.(); }}
      />
      <ShootDayDialog
        state={shootDialog}
        accountKey={accountKey}
        isAdmin={isAdmin}
        members={members}
        events={events}
        onClose={() => setShootDialog(null)}
        onChanged={loadShootDays}
        onOpenEvent={(ev) => { setShootDialog(null); setEventDialog({ existing: ev }); }}
      />
      <EventDialog
        state={eventDialog}
        accountKey={accountKey}
        isAdmin={isAdmin}
        members={members}
        onClose={() => setEventDialog(null)}
        onChanged={loadEvents}
      />
      <CalendarSubscriptionDialog open={icalDialog} accountKey={accountKey} channelName={current?.name || accountKey} onClose={() => setIcalDialog(false)} />
    </div>
  );
}

function EventDialog({ state, accountKey, isAdmin, members = [], onClose, onChanged }) {
  const existing = state?.existing || null;
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!state) { setForm(null); return; }
    setForm(existing
      ? null
      : { title: "", date: format(state.day, "yyyy-MM-dd"), time: "", location: "", description: "", participant_ids: [] });
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!state) return null;

  const save = async () => {
    if (!form.title.trim() || !form.date) return toast.error("Titel und Datum sind Pflicht");
    setSaving(true);
    try {
      await api.post("/events", { account_key: accountKey, ...form, title: form.title.trim() });
      toast.success("Event angelegt — auch im Event-Radar sichtbar 📅");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Anlegen fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Event „${existing.title}" wirklich löschen?`)) return;
    await api.delete(`/events/${existing.id}`);
    toast.success("Event gelöscht");
    onChanged();
    onClose();
  };

  const inputCls = "mt-1 w-full h-10 px-3 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400";

  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="calevent-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-emerald-600" /> {existing ? existing.title : "Event anlegen"}
          </DialogTitle>
          <DialogDescription>
            {existing ? "Details aus dem Event-Radar." : "Erscheint im Kalender und im Event-Radar — ganz ohne Pipeline."}
          </DialogDescription>
        </DialogHeader>
        {existing ? (
          <div className="space-y-2.5 text-sm text-neutral-700">
            <p className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-neutral-400" />
              {(() => { try { return format(parseISO(existing.date), "EEEE, d. MMMM yyyy", { locale: de }); } catch { return existing.date; } })()}
              {existing.time ? ` · ${existing.time} Uhr` : ""}
            </p>
            {existing.location && <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-neutral-400" /> {existing.location}</p>}
            {existing.description && <p className="text-neutral-500 whitespace-pre-line">{existing.description}</p>}
            {(existing.participant_names || []).length > 0 && (
              <p className="text-neutral-500">Teilnehmende: {existing.participant_names.join(", ")}</p>
            )}
            <p className="text-[11px] text-neutral-400">
              Quelle: {existing.source === "website" ? "Webseite" : existing.source === "stadt" ? "Stadt Bruchköbel" : existing.source === "upload" ? "Upload" : existing.source === "office" ? "Office-Board" : "Manuell"}
              {existing.source === "office" && (
                <span data-testid="calevent-office-badge" className="ml-2 inline-flex items-center gap-1 bg-sky-50 text-sky-700 border border-sky-200 rounded-full px-2 py-0.5 font-semibold">
                  🏢 Office{existing.synced_at ? (() => { try { return ` · Sync ${format(parseISO(existing.synced_at), "dd.MM. HH:mm")}`; } catch { return ""; } })() : ""}
                </span>
              )}
            </p>
            <div className="flex items-center gap-2 pt-1">
              <button
                data-testid="calevent-plan-content"
                onClick={async () => {
                  try {
                    const { data } = await api.post(`/events/${existing.id}/plan-content`, { account_key: accountKey, content_type: "reel", with_ai: false });
                    toast.success(data.existing ? "Für dieses Event gibt es bereits eine Pipeline-Karte" : "Content-Idee in der Pipeline angelegt 💡");
                    onClose();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Anlegen fehlgeschlagen");
                  }
                }}
                className="h-9 px-3 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 flex items-center gap-1.5"
              >
                <Plus className="h-4 w-4" /> Content-Idee anlegen
              </button>
              {isAdmin && (
                <button data-testid="calevent-delete" onClick={remove}
                  className="h-9 px-3 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 text-sm font-medium flex items-center gap-1.5">
                  <Trash2 className="h-4 w-4" /> Löschen
                </button>
              )}
            </div>
          </div>
        ) : form && (
          <div className="space-y-3.5">
            <div>
              <label className="text-xs font-medium text-neutral-600">Titel</label>
              <input data-testid="calevent-title" value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="z.B. Weinfest am Marktplatz" className={inputCls} />
            </div>
            <div className="grid grid-cols-3 gap-2.5">
              <div>
                <label className="text-xs font-medium text-neutral-600">Datum</label>
                <input data-testid="calevent-date" type="date" value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-600 flex items-center gap-1"><Clock className="h-3 w-3" /> Uhrzeit</label>
                <input data-testid="calevent-time" type="time" value={form.time}
                  onChange={(e) => setForm({ ...form, time: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-600">Ort</label>
                <input data-testid="calevent-location" value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="optional" className={inputCls} />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600">Beschreibung</label>
              <textarea data-testid="calevent-description" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3} placeholder="optional"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400" />
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600">Teilnehmende</label>
              <div className="mt-1 max-h-32 overflow-y-auto rounded-lg border border-neutral-200 p-2 space-y-1.5">
                {members.map((member) => {
                  const id = String(member.id || member._id);
                  return (
                    <label key={id} className="flex items-center gap-2 text-sm text-neutral-700 cursor-pointer">
                      <input type="checkbox" checked={(form.participant_ids || []).includes(id)}
                        onChange={() => setForm({ ...form, participant_ids: (form.participant_ids || []).includes(id)
                          ? form.participant_ids.filter((value) => value !== id)
                          : [...(form.participant_ids || []), id] })} />
                      {member.name}
                    </label>
                  );
                })}
                {members.length === 0 && <p className="text-xs text-neutral-400">Keine Teammitglieder gefunden.</p>}
              </div>
              <p className="mt-1 text-[11px] text-neutral-400">Wird für „Nur meine Termine“ im persönlichen iCal-Abo verwendet.</p>
            </div>
            <button data-testid="calevent-save" onClick={save} disabled={saving}
              className="w-full h-10 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Event anlegen
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CalendarSubscriptionDialog({ open, accountKey, channelName, onClose }) {
  const [scope, setScope] = useState("mine");
  const [creating, setCreating] = useState(false);
  const [feed, setFeed] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => { if (!open) { setFeed(null); setCopied(false); } }, [open]);

  const createFeed = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/calendar-feeds", { account_key: accountKey, scope });
      setFeed(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kalender-Abo konnte nicht erstellt werden");
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(feed.url);
    setCopied(true);
    toast.success("Kalender-Link kopiert");
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-w-lg" data-testid="ical-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2"><Link2 className="h-5 w-5 text-blue-600" /> Kalender automatisch abonnieren</DialogTitle>
          <DialogDescription>Nur der Kanal „{channelName}“. Änderungen werden über denselben Link automatisch aktualisiert.</DialogDescription>
        </DialogHeader>
        {!feed ? (
          <div className="space-y-4">
            <div className="grid gap-2">
              <label className={`rounded-xl border p-3 cursor-pointer ${scope === "mine" ? "border-blue-400 bg-blue-50" : "border-neutral-200"}`}>
                <input className="mr-2" type="radio" name="ical-scope" value="mine" checked={scope === "mine"} onChange={() => setScope("mine")} />
                <span className="font-semibold text-sm">Nur meine Creator-/Diensttermine</span>
                <p className="ml-6 mt-1 text-xs text-neutral-500">Nur Termine, bei denen du als Creator oder teilnehmende Person hinterlegt bist.</p>
              </label>
              <label className={`rounded-xl border p-3 cursor-pointer ${scope === "all" ? "border-blue-400 bg-blue-50" : "border-neutral-200"}`}>
                <input className="mr-2" type="radio" name="ical-scope" value="all" checked={scope === "all"} onChange={() => setScope("all")} />
                <span className="font-semibold text-sm">Alle Termine des Kanals</span>
                <p className="ml-6 mt-1 text-xs text-neutral-500">Alle Events, Drehtage und geplanten Veröffentlichungen – ohne andere Kanäle.</p>
              </label>
            </div>
            <button data-testid="ical-create" onClick={createFeed} disabled={creating}
              className="w-full h-11 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-60 flex items-center justify-center gap-2">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarCheck className="h-4 w-4" />} Abo-Link erstellen
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
              Der private Abo-Link ist bereit. Bitte nicht öffentlich weitergeben – wer ihn besitzt, kann die Termine dieses Kalenders lesen.
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button onClick={copyLink} className="flex-1 h-10 rounded-lg border border-neutral-200 font-semibold text-sm flex items-center justify-center gap-2 hover:bg-neutral-50">
                {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />} {copied ? "Kopiert" : "Abo-Link kopieren"}
              </button>
              <a href={feed.webcal_url} className="flex-1 h-10 rounded-lg bg-blue-600 text-white font-semibold text-sm flex items-center justify-center gap-2 hover:bg-blue-700">
                <ExternalLink className="h-4 w-4" /> Kalender öffnen
              </a>
            </div>
            <p className="text-xs text-neutral-500">Apple Kalender und Outlook aktualisieren automatisch. Bei Google Kalender den kopierten Link unter „Weitere Kalender → Per URL“ hinzufügen.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ShootDayDialog({ state, accountKey, isAdmin, members, events = [], onClose, onChanged, onOpenEvent }) {
  const existing = state?.existing || null;
  const editable = isAdmin;
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!state) { setForm(null); return; }
    if (existing) {
      setForm({
        title: existing.title, date: existing.date, time_from: existing.time_from || "",
        time_to: existing.time_to || "", location: existing.location || "",
        notes: existing.notes || "", creator_ids: existing.creator_ids || [],
        event_id: existing.event_id || "",
      });
    } else {
      setForm({
        title: "", date: format(state.day, "yyyy-MM-dd"), time_from: "", time_to: "",
        location: "", notes: "", creator_ids: [], event_id: "",
      });
    }
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!state || !form) return null;

  const toggleCreator = (id) =>
    setForm((f) => ({
      ...f,
      creator_ids: f.creator_ids.includes(id) ? f.creator_ids.filter((x) => x !== id) : [...f.creator_ids, id],
    }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Bitte einen Titel angeben");
    setSaving(true);
    try {
      if (existing) {
        await api.patch(`/shoot-days/${existing.id}`, form);
        toast.success("Drehtag aktualisiert");
      } else {
        await api.post("/shoot-days", { ...form, account_key: accountKey });
        toast.success("Drehtag angelegt — zugewiesene Creator wurden benachrichtigt 🎬");
      }
      onChanged();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Diesen Drehtag wirklich löschen?")) return;
    await api.delete(`/shoot-days/${existing.id}`);
    toast.success("Drehtag gelöscht");
    onChanged();
    onClose();
  };

  const downloadIcs = async () => {
    try {
      const { data } = await api.get(`/shoot-days/${existing.id}/ics`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/calendar" }));
      const a = document.createElement("a");
      a.href = url; a.download = `drehtag-${existing.date}.ics`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success("Kalenderdatei geladen — öffnen, um den Termin zu speichern 📆");
    } catch { toast.error("Export fehlgeschlagen"); }
  };

  const inputCls = "mt-1 w-full h-10 px-3 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 disabled:bg-neutral-50 disabled:text-neutral-500";

  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="shootday-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2">
            <Clapperboard className="h-5 w-5 text-amber-600" />
            {existing ? (editable ? "Drehtag bearbeiten" : "Drehtag") : "Drehtag anlegen"}
          </DialogTitle>
          <DialogDescription>
            {editable ? "Das Team sieht den Drehtag im Kalender — zugewiesene Creator bekommen eine Mitteilung." : "Plane dir für diesen Termin Zeit ein."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5">
          <div>
            <label className="text-xs font-medium text-neutral-600">Titel</label>
            <input data-testid="shootday-title" disabled={!editable} value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="z.B. Dreh Hafenfest — Reels & Stories" className={inputCls} />
          </div>
          <div className="grid grid-cols-3 gap-2.5">
            <div>
              <label className="text-xs font-medium text-neutral-600">Datum</label>
              <input data-testid="shootday-date" type="date" disabled={!editable} value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600 flex items-center gap-1"><Clock className="h-3 w-3" /> Von</label>
              <input data-testid="shootday-from" type="time" disabled={!editable} value={form.time_from}
                onChange={(e) => setForm({ ...form, time_from: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600">Bis</label>
              <input data-testid="shootday-to" type="time" disabled={!editable} value={form.time_to}
                onChange={(e) => setForm({ ...form, time_to: e.target.value })} className={inputCls} />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-neutral-600 flex items-center gap-1"><MapPin className="h-3 w-3" /> Ort</label>
            <input data-testid="shootday-location" disabled={!editable} value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="z.B. LOKAL, Innenstadt" className={inputCls} />
          </div>
          <div>
            <label className="text-xs font-medium text-neutral-600">Notizen / Was wird gedreht?</label>
            <textarea data-testid="shootday-notes" disabled={!editable} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3} placeholder="Equipment, Ablauf, Ansprechpartner…"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 disabled:bg-neutral-50 disabled:text-neutral-500" />
          </div>
          <div>
            <label className="text-xs font-medium text-neutral-600">Verknüpftes Event (optional)</label>
            {editable ? (
              <select
                data-testid="shootday-event"
                value={form.event_id}
                onChange={(e) => setForm({ ...form, event_id: e.target.value })}
                className={inputCls}
              >
                <option value="">Kein Event</option>
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.title}{ev.date ? ` (${(() => { try { return format(parseISO(ev.date), "dd.MM."); } catch { return ev.date; } })()})` : ""}
                  </option>
                ))}
              </select>
            ) : existing?.event_id ? (
              <button
                data-testid="shootday-open-event"
                onClick={() => {
                  const ev = events.find((x) => x.id === existing.event_id);
                  if (ev) onOpenEvent(ev);
                  else toast.error("Event nicht mehr vorhanden");
                }}
                className="mt-1 w-full text-left px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-sm text-emerald-800 font-medium hover:bg-emerald-100"
              >
                📅 {existing.event_title || "Event ansehen"} →
              </button>
            ) : (
              <p className="mt-1 text-sm text-neutral-400">Kein Event verknüpft</p>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-neutral-600">Creator (bekommen eine Push-Mitteilung)</label>
            {editable ? (
              <div className="mt-1.5 space-y-1.5 max-h-36 overflow-y-auto rounded-lg border border-neutral-200 p-2.5">
                {members.map((m) => (
                  <label key={m.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" data-testid={`shootday-creator-${m.id}`}
                      checked={form.creator_ids.includes(m.id)}
                      onChange={() => toggleCreator(m.id)} className="accent-amber-600 h-4 w-4" />
                    <span>{m.name}</span>
                    <span className="text-[11px] text-neutral-400">{m.role === "admin" ? "Redaktion" : "Creator"}</span>
                  </label>
                ))}
                {members.length === 0 && <p className="text-xs text-neutral-400">Keine Teammitglieder gefunden.</p>}
                <p className="text-[11px] text-neutral-400 pt-1">Niemand ausgewählt = ganzes Team wird informiert.</p>
              </div>
            ) : (
              <p className="mt-1 text-sm text-neutral-700" data-testid="shootday-creators-view">
                {(existing?.creator_names || []).length ? existing.creator_names.join(", ") : "Ganzes Team"}
              </p>
            )}
          </div>
          {existing && (
            <button data-testid="shootday-ics" onClick={downloadIcs}
              className="w-full h-10 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm font-semibold hover:bg-amber-100 flex items-center justify-center gap-2">
              <CalendarCheck className="h-4 w-4" /> In meinen Kalender übernehmen (.ics)
            </button>
          )}
          {editable && (
            <div className="flex items-center gap-2 pt-1">
              <button data-testid="shootday-save" onClick={save} disabled={saving}
                className="flex-1 h-10 rounded-lg bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 flex items-center justify-center gap-2 disabled:opacity-60">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {existing ? "Speichern" : "Drehtag anlegen"}
              </button>
              {existing && (
                <button data-testid="shootday-delete" onClick={remove}
                  className="h-10 w-10 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 flex items-center justify-center">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ScheduleDialog({ day, items, onClose, onScheduled }) {
  const [busy, setBusy] = useState("");
  if (!day) return null;
  const dayStr = format(day, "yyyy-MM-dd");
  // schedulable = anything not yet published and not already on this day
  const candidates = items.filter(
    (i) => i.status !== "published" && !(i.scheduled_date && isSameDay(parseISO(i.scheduled_date), day)),
  );

  const schedule = async (item) => {
    setBusy(item.id);
    try {
      await api.patch(`/content/${item.id}`, { scheduled_date: dayStr, status: "scheduled" });
      toast.success(`„${item.title}" für ${format(day, "d. MMMM", { locale: de })} eingeplant`);
      onScheduled();
      onClose();
    } catch {
      toast.error("Einplanen fehlgeschlagen");
    } finally {
      setBusy("");
    }
  };

  return (
    <Dialog open={!!day} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="schedule-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2">
            <CalendarPlus className="h-5 w-5 text-blue-600" /> Inhalt einplanen
          </DialogTitle>
          <DialogDescription>
            Für {format(day, "EEEE, d. MMMM yyyy", { locale: de })} — wähle einen Inhalt aus.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[55vh] overflow-y-auto">
          {candidates.length === 0 && (
            <p className="text-sm text-neutral-400 py-6 text-center">Keine planbaren Inhalte. Lege zuerst Inhalte in der Pipeline an.</p>
          )}
          {candidates.map((item) => (
            <button
              key={item.id}
              data-testid={`schedule-item-${item.id}`}
              onClick={() => schedule(item)}
              disabled={busy === item.id}
              className="w-full text-left flex items-center gap-3 p-2.5 rounded-lg border border-neutral-200 hover:border-blue-300 hover:bg-blue-50/40 transition disabled:opacity-50"
            >
              {item.media_url && <img src={thumbUrl(item.media_url, 160)} alt="" className="h-10 w-8 rounded object-cover" />}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-neutral-900 truncate">{item.title}</div>
                <div className="text-[11px] text-neutral-400">{TYPE_LABEL[item.content_type]} · {STATUS_LABEL[item.status]}</div>
              </div>
              <Plus className="h-4 w-4 text-blue-500" />
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
